'use client'

import NextLink from 'next/link'
import { useParams as useNextParams, usePathname, useRouter, useSearchParams } from 'next/navigation'

/** The address's ?query values. Screens using it must sit inside <Suspense> (see the route's page.tsx). */
export { useSearchParams }
import { useEffect, type ComponentProps, type ReactNode } from 'react'

/**
 * The small part of React Router the screens use, on top of Next.js navigation.
 * Keeping the same names means the screens did not need rewriting when moving to Next.js.
 */

/**
 * Next.js has no "navigation state" (React Router keeps it in browser history), so state passed to
 * navigate() is kept in sessionStorage for the page it was sent to: e.g. the page to return to
 * after signing in, or upload problems to show on a newly created project.
 */
const STATE_KEY = 'gcrm:navigation-state'

function readAll(): Record<string, unknown> {
  try {
    return JSON.parse(sessionStorage.getItem(STATE_KEY) ?? '{}') as Record<string, unknown>
  } catch {
    return {}
  }
}

/**
 * One entry per destination. Navigating somewhere replaces only that page's entry: Next.js changes
 * pages asynchronously, so the page being left may render once more and must still see its own state.
 */
function writeState(path: string, state: unknown) {
  try {
    const all = readAll()
    if (state === undefined) delete all[path]
    else all[path] = state
    sessionStorage.setItem(STATE_KEY, JSON.stringify(all))
  } catch {
    // Storage blocked (private mode): the page just shows without the extra state.
  }
}

function readState(path: string): unknown {
  return readAll()[path] ?? null
}

type To = string

type LinkProps = Omit<ComponentProps<typeof NextLink>, 'href'> & { to: To }

export function Link({ to, ...rest }: LinkProps) {
  return <NextLink href={to} {...rest} />
}

type NavLinkProps = Omit<LinkProps, 'className'> & {
  className?: string
  /** Only active on exactly this path (not on paths below it). */
  end?: boolean
  children?: ReactNode
}

/** A link that gets the "active" class when its page is showing (styled in index.css). */
export function NavLink({ to, end, className, ...rest }: NavLinkProps) {
  const pathname = usePathname()
  const active = end || to === '/' ? pathname === to : pathname === to || pathname.startsWith(`${to}/`)
  return (
    <NextLink
      href={to}
      className={[className, active ? 'active' : null].filter(Boolean).join(' ')}
      aria-current={active ? 'page' : undefined}
      {...rest}
    />
  )
}

type NavigateOptions = { replace?: boolean; state?: unknown }

export function useNavigate() {
  const router = useRouter()
  return (to: To | number, options: NavigateOptions = {}) => {
    if (typeof to === 'number') {
      router.back()
      return
    }
    writeState(to, options.state)
    if (options.replace) router.replace(to)
    else router.push(to)
  }
}

/** Redirects as soon as it is shown. */
export function Navigate({ to, replace, state }: { to: To } & NavigateOptions) {
  const navigate = useNavigate()
  useEffect(() => {
    navigate(to, { replace, state })
    // Run once per destination.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [to])
  return null
}

export function useLocation(): { pathname: string; state: any } {
  const pathname = usePathname()
  // On the server (prerendering) there is no navigation state.
  return { pathname, state: typeof window === 'undefined' ? null : readState(pathname) }
}

export function useParams(): Record<string, string | undefined> {
  const params = useNextParams() ?? {}
  return Object.fromEntries(Object.entries(params).map(([k, v]) => [k, Array.isArray(v) ? v[0] : v]))
}
